import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CompensationMultiplier } from '../../../models/compensationMultiplier';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-compensation-multiplier-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './compensation-multiplier-list.component.html',
  styleUrl: './compensation-multiplier-list.component.scss'
})
export class CompensationMultiplierListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  compensationMultipliers = signal<CompensationMultiplier[]>([]);
  rightItem: CompensationMultiplier | undefined = undefined;
  matchedItems: CompensationMultiplier[] | undefined = undefined;

  // Define custom columns to show value instead of name
  columns: ColumnConfig[] = [
    { key: 'value', header: 'Value', type: 'text', sortable: true, editable: true },
    { key: 'priority', header: 'Priority', type: 'boolean', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCompensationMultipliers();
  }

  loadCompensationMultipliers(): void {
    this.spinnerService.show();
    this.lutService.getCompensationMultipliers().subscribe({
      next: (data) => {
        // Sort by value before setting
        const sortedData = this.sortCompensationMultipliers(data);
        this.compensationMultipliers.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading compensation multipliers:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCompensationMultipliers(compensationMultipliers: CompensationMultiplier[]): CompensationMultiplier[] {
    return [...compensationMultipliers].sort((a, b) => {
      // Priority items first, then sort by value
      if (a.priority !== b.priority) {
        return a.priority ? -1 : 1;
      }
      return a.value - b.value;
    });
  }

  onCreateCompensationMultiplier(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(compensationMultiplier: CompensationMultiplier): void {
  }

  onEditCompensationMultiplier(compensationMultiplier: CompensationMultiplier): void {
  }

  onDeleteCompensationMultiplier(compensationMultiplier: CompensationMultiplier): void {
    // Use value for display since name is null
    const displayName = `${compensationMultiplier.value}`;
    this.confirmationDialogService.openDeleteConfirmationDialog(displayName)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCompensationMultipliers = this.compensationMultipliers();

          // 1. Optimistically remove from local state immediately
          this.compensationMultipliers.update(compensationMultipliers => compensationMultipliers.filter(cm => cm.id !== compensationMultiplier.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCompensationMultiplier(compensationMultiplier.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Compensation multiplier deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting compensation multiplier:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.compensationMultipliers.set(originalCompensationMultipliers);
              this.toast.error(`<strong>Failed to delete Compensation Multiplier:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CompensationMultiplier, newName: string} | {item: CompensationMultiplier, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: CompensationMultiplier;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      // Convert string to number for value field
      const newValue = event.columnKey === 'value' ? parseFloat(event.newValue) : event.newValue;
      updatedItem = { ...event.item, [event.columnKey]: newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.compensationMultipliers.update(compensationMultipliers => {
      const updated = compensationMultipliers.map(cm => cm.id === event.item.id ? updatedItem : cm);
      return this.sortCompensationMultipliers(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCompensationMultiplier(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation multiplier updated successfully');
        // 3. On success: Update with server response and re-sort to maintain order
        this.compensationMultipliers.update(compensationMultipliers => {
          const updated = compensationMultipliers.map(cm => cm.id === event.item.id ? response : cm);
          return this.sortCompensationMultipliers(updated);
        });
      },
      error: (error) => {
        console.error('Error updating compensation multiplier:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.compensationMultipliers.update(compensationMultipliers =>
          compensationMultipliers.map(cm => cm.id === event.item.id ? originalItem : cm)
        );
        this.toast.error(`<strong>Failed to update Compensation Multiplier:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const valueAsNumber = parseFloat(event['value']);
    const priorityValue = event['priority'] ?? false;
    const newCompensationMultiplier: CompensationMultiplier = { id: tempId, name: '', value: valueAsNumber, priority: priorityValue, systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.compensationMultipliers.update(compensationMultipliers => this.sortCompensationMultipliers([...compensationMultipliers, newCompensationMultiplier]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCompensationMultiplier({ name: '', value: valueAsNumber, priority: priorityValue, systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Compensation multiplier created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain order
        this.compensationMultipliers.update(compensationMultipliers => {
          const updated = compensationMultipliers.map(cm => cm.id === tempId ? response : cm);
          return this.sortCompensationMultipliers(updated);
        });
      },
      error: (error) => {
        console.error('Error creating compensation multiplier:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.compensationMultipliers.update(compensationMultipliers =>
          compensationMultipliers.filter(cm => cm.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Compensation Multiplier:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: CompensationMultiplier, matchedItems?: CompensationMultiplier[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onCompensationMultiplierCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog - use value for display since name is null
    const itemsToMerge = this.matchedItems.map(item => item.value.toString()).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.value}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalCompensationMultipliers = this.compensationMultipliers();

        // 1. Optimistically remove merged items from local state
        this.compensationMultipliers.update(compensationMultipliers =>
          compensationMultipliers.filter(cm => !bulkMergeRequest.leftIds.includes(cm.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeCompensationMultipliersBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Compensation multipliers merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadCompensationMultipliers();
          },
          error: (error) => {
            console.error('Error merging compensation multipliers:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.compensationMultipliers.set(originalCompensationMultipliers);
            // Restore selection state for retry
            this.rightItem = originalCompensationMultipliers.find(cm => cm.id === bulkMergeRequest.rightId);
            this.matchedItems = originalCompensationMultipliers.filter(cm => bulkMergeRequest.leftIds.includes(cm.id));
            this.toast.error(`<strong>Failed to merge Compensation Multipliers:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
